<template>
  <section
    class="pt-6"
    data-id="plan-displayer"
    :data-render-index="renderIndex"
  >
    <CardGrouping size="xl">
      <div class="relative">
        <PricingGrid class="top-4 lg:sticky">
          <div
            class="align-center hidden flex-col items-center justify-center gap-3 bg-white lg:flex"
            v-if="!subscription"
          >
            <p class="text-left italic text-gray-600">
              {{
                pricingParams
                  ? 'Matching plans'
                  : 'Select a plan to get started'
              }}
            </p>
            <img src="/icons/arrow-dr.svg" alt="" width="80px" />
          </div>
          <span v-else />
          <PlanCard
            v-for="plan in plans"
            :key="plan.planId"
            :disabled="isDisabled(plan)"
            :plan="plan"
            :pricingParams="pricingParams"
          />
        </PricingGrid>
        <!-- top -->
        <PricingGrid>
          <div class="hidden lg:block" />
          <div
            class="mt-4 hidden rounded-t-lg bg-gray-50 p-2 lg:block"
            v-for="plan in plans"
            :key="'top-' + plan.planId"
          />
        </PricingGrid>
        <div class="hidden divide-y divide-gray-100 lg:block">
          <!-- limits -->
          <PricingGrid
            class="align-center items-center justify-center"
            v-for="limit in limits"
            :key="'limit-' + limit"
            :data-pg-limit="limit"
            :highlight="
              pricingParams ? pricingParams.highlightLimit === limit : undefined
            "
          >
            <PlanLabel :limit="limit" />
            <PricingCell
              v-for="plan in plans"
              :key="limit + '-' + plan.planId"
              :highlight="
                pricingParams
                  ? pricingParams.highlightLimit === limit
                  : undefined
              "
            >
              <PlanLimitValue
                :value="plan.limits[limit]"
                :per-month="limit.includes('monthly')"
              />
            </PricingCell>
          </PricingGrid>
          <!-- addons -->
          <PricingGrid
            class="align-center items-center justify-center"
            v-for="addon in addons"
            :key="'addon-' + addon"
            :data-pg-addon="addon"
            :highlight="
              pricingParams ? pricingParams.highlightAddon === addon : undefined
            "
          >
            <PlanLabel :addon="addon" />
            <PricingCell
              v-for="plan in plans"
              :key="addon + '-' + plan.planId"
              :highlight="
                pricingParams
                  ? pricingParams.highlightAddon === addon
                  : undefined
              "
            >
              <PlanLimitValueBoolean
                :value="plan.addons.supportedAddons.indexOf(addon) > -1"
              />
            </PricingCell>
          </PricingGrid>
          <!-- features -->
          <PricingGrid
            class="align-center items-center justify-center"
            v-for="feature in features"
            :key="'feature-' + feature"
            :data-pg-feature="feature"
            :highlight="
              pricingParams
                ? pricingParams.highlightFeature === feature
                : undefined
            "
          >
            <PlanLabel :feature="feature" />
            <PricingCell
              v-for="plan in plans"
              :key="feature + '-' + plan.planId"
              :highlight="
                pricingParams
                  ? pricingParams.highlightFeature === feature
                  : undefined
              "
            >
              <PlanLimitValueBoolean :value="plan.features[feature]" />
            </PricingCell>
          </PricingGrid>
        </div>
        <!-- bottom -->
        <PricingGrid>
          <div class="hidden lg:block" />
          <div
            class="hidden rounded-b-lg bg-gray-50 p-2 lg:block"
            v-for="plan in plans"
            :key="'top-' + plan.planId"
          />
        </PricingGrid>
        <div class="mt-4 hidden text-center lg:block">
          <button
            class="text-link text-sm"
            type="button"
            @click="showExtraFeatures = !showExtraFeatures"
          >
            {{ showExtraFeatures ? 'Hide' : 'Show' }}
            extra features
            <Icon :icon="showExtraFeatures ? 'chevronUp' : 'chevronDown'" />
          </button>
        </div>
      </div>
      <AddonSection
        subtitle="Add additional features and usage limits to your base plan."
        title="Pay as you go features"
      >
        <template #full>
          <div class="flex w-full flex-col gap-5 lg:flex-row">
            <AddonSMS class="flex-1" :change-index="changeIndex" />
            <AddonUserPlans class="self-stretch" :change-index="changeIndex" />
          </div>
        </template>
        <AddonEmailPlans class="self-stretch" :change-index="changeIndex" />
        <AddonInboxPlans class="self-stretch" :change-index="changeIndex" />
        <AddonAIPlans class="self-stretch" :change-index="changeIndex" />
        <AddonGuestPlans class="self-stretch" :change-index="changeIndex" />
        <AddonConnectorPlans class="self-stretch" :change-index="changeIndex" />
        <AddonEmailVerification
          class="self-stretch"
          :change-index="changeIndex"
        />
      </AddonSection>
    </CardGrouping>
    <AlertCard
      class="mt-8"
      flat
      :links="[
        {
          text: 'Speak to sales',
          href: support,
          targetBlank: true,
          icon: 'arrowRight',
        },
      ]"
      :variant="Variant.info"
      title="Got questions?"
      text="Let's jump on a call and discuss your needs and requirements."
    />
  </section>
</template>

<script setup lang="ts">
import {
  type PlanDisplay,
  type PlanFeatures,
  type PlanLimits,
} from '@mailslurp/admin-sdk';
import { links } from '@mailslurp/common/dist/links';
import PlanCard from '../../components/pricing/PlanCard.vue';
import AddonSMS from './addon/AddonSMS.vue';
import AddonEmailVerification from './addon/AddonEmailVerification.vue';
import type { PricingQueryParams } from '../../lib/services/helpers';
import { onMounted, ref, computed } from 'vue';
import { useStore3 } from '../../store/main';
import AddonEmailPlans from './addon/AddonEmailPlans.vue';
import AddonUserPlans from './addon/AddonUserPlans.vue';
import AddonConnectorPlans from './addon/AddonConnectorPlans.vue';
import AddonAIPlans from './addon/AddonAIPlans.vue';
import PlanLimitValue from '../form/PlanLimitValue.vue';
import { Variant } from '../../store/types';
import AlertCard from '../tw/AlertCard.vue';
import PlanLabel from './PlanLabel.vue';
import CardGrouping from '../tw/CardGrouping.vue';
import { storeToRefs } from 'pinia';
import AddonSection from './AddonSection.vue';
import PricingGrid from '../../components/modules/PricingGrid.vue';
import PlanLimitValueBoolean from '../../components/form/PlanLimitValueBoolean.vue';
import Icon from '../../components/form/Icon.vue';
import { PlanAddonsSupportedAddonsEnum } from '@mailslurp/admin-sdk';
import AddonInboxPlans from './addon/AddonInboxPlans.vue';
import PricingCell from '../../components/modules/PricingCell.vue';
import AddonGuestPlans from './addon/AddonGuestPlans.vue';

const store3 = useStore3();

const showExtraFeatures = ref(false);
const support = ref(links.sales.url);
const changeIndex = ref<number>(1);
// should the plans be expanded on start?

const props = withDefaults(
  defineProps<{
    renderIndex?: number;
    pricingParams?: null | PricingQueryParams;
  }>(),
  {
    renderIndex: 0,
    pricingParams: undefined,
  },
);

watch(
  () => props.pricingParams,
  () => {
    if (props.pricingParams) {
      showExtraFeatures.value = true;
    }
  },
);

console.log(props.pricingParams);
const { plans: planDisplays, subscription } = storeToRefs(store3);
const plans = computed<PlanDisplay[]>(() => {
  return planDisplays.value ?? [];
});
const SHOW_ALL_FEATURES = true;
const omittedFeatures: (keyof PlanFeatures)[] = [];
const omittedLimits: (keyof PlanLimits)[] = [
  'monthlyGetMailServerRecords',
  'monthlyVerifyEmailAddress',
  // 'organizations',
  'phoneNumbers',
  'maxUniqueRecipients',
  'recipients',
];
const extraLimits: (keyof PlanLimits)[] = [
  'organizations',
  'aliases',
  'threads',
];

const extraFeatures = computed<(keyof PlanFeatures)[]>(() => {
  return ['phonePlans', 'userPlans', ...featuresInEveryPlan.value];
});
onMounted(() => {
  if (props.pricingParams) {
    showExtraFeatures.value = true;
  }
});
// onMounted(() => {
//   // highlight pricing table features
//   const v = props.highlightFeature ?? props.highlightLimit;
//   if (v) {
//     try {
//       document
//         .getElementById(pricingFeatureTableRowId(v))
//         ?.scrollIntoView({ behavior: 'smooth' });
//     } catch (e) {}
//   }
// });
onMounted(() => {
  if (props.pricingParams?.highlightFeature) {
    scrollTo('data-pg-feature', props.pricingParams.highlightFeature);
  } else if (props.pricingParams?.highlightLimit) {
    scrollTo('data-pg-limit', props.pricingParams.highlightLimit);
  } else if (props.pricingParams?.highlightAddon) {
    scrollTo('data-pg-addon', props.pricingParams.highlightAddon);
  }
});

const scrollTo = (attr: string, value: string) => {
  const el = document.querySelector(`[${attr}="${value}"]`);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const featuresInEveryPlan = computed<(keyof PlanFeatures)[]>(() => {
  if (SHOW_ALL_FEATURES) {
    return [];
  }
  // iterate plan displays and find the features that are enabled for every plan
  const features: Map<keyof PlanFeatures, number> = new Map<
    keyof PlanFeatures,
    number
  >();
  planDisplays?.value?.forEach?.((plan) => {
    Object.keys(plan.features).forEach((feature) => {
      if (plan?.features && feature in plan.features) {
        features.set(
          feature as keyof PlanFeatures,
          (features.get(feature as keyof PlanFeatures) || 0) + 1,
        );
      }
    });
  });
  return Array.from(features.entries())
    .filter(([_, count]) => count === planDisplays?.value?.length)
    .map(([feature, _]) => feature);
});

const limits = computed<(keyof PlanLimits)[]>(() => {
  return (
    Object.keys(
      planDisplays.value?.at?.(0)?.limits ?? {},
    ) as (keyof PlanLimits)[]
  ).filter(
    (it: keyof PlanLimits) =>
      !omittedLimits.includes(it) &&
      (showExtraFeatures.value || !extraLimits.includes(it)),
  ) as (keyof PlanLimits)[];
});
const addons = computed<PlanAddonsSupportedAddonsEnum[]>(() => {
  return Object.values(PlanAddonsSupportedAddonsEnum);
});
const features = computed<(keyof PlanFeatures)[]>(() => {
  return (
    Object.keys(
      planDisplays.value?.at?.(0)?.features ?? {},
    ) as (keyof PlanFeatures)[]
  ).filter(
    (it: keyof PlanFeatures) =>
      !omittedFeatures.includes(it) &&
      (showExtraFeatures.value || !extraFeatures.value.includes(it)),
  ) as (keyof PlanFeatures)[];
});

const isDisabled = (plan: PlanDisplay) => {
  const subType = (plan.subscriptionType || '').toString();
  return !!(
    subscription.value &&
    subscription.value.externalPlanId === plan.planId &&
    subscription.value.subscriptionSchedule.toString() ===
      plan.schedule.toString() &&
    subscription.value.subscriptionType.toString() === subType
  );
};
</script>
